import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Camera,
  ChevronDown,
  ClipboardList,
  MessageCirclePlus,
  MessageCircle,
  Paperclip,
} from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, cn } from '@jeraisonne/ui';
import { SUBJECTS } from '@jeraisonne/db';
import type { TutorAssignmentSummary } from '@/lib/student-objectives/objective-types';
import { AvatarCircle } from '@/components/avatar-circle';
import { DocumentsPanel } from '@/components/documents/documents-panel';
import { DiscussionsList } from '@/features/discussions/discussions-list';
import { useCreateDiscussion, useDiscussions } from '@/lib/discussions/queries';
import { hasUnreadDiscussion } from '@/lib/discussions/discussion-unread';
import { DiscussionError } from '@/lib/discussions/types';
import { useAuth } from '@/lib/auth';
import { ExerciseAccessBadge } from './exercise-access-badge';

interface Props {
  summary: TutorAssignmentSummary;
  onStartExercise: (exerciseId: string) => void;
  /** Exercice en cours d'ouverture (spinner sur son bouton). */
  pendingExerciseId?: string | null;
  /** Ouvre la modale d'envoi de photo d'exercice à ce prof. */
  onSendExercise: () => void;
}

export function TutorCard({
  summary,
  onStartExercise,
  onSendExercise,
  pendingExerciseId = null,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  // Le panneau n'est monté qu'à l'ouverture : N profs = 0 requête tant qu'on n'y touche pas.
  const [showFiles, setShowFiles] = useState(false);
  const [showDiscussions, setShowDiscussions] = useState(false);
  const [discussionError, setDiscussionError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  // Requête PARTAGÉE entre toutes les cartes prof (même clé de cache) : une
  // seule requête réseau, pas N — sert aussi le badge non-lu même repliée.
  const discussions = useDiscussions();
  const createDiscussion = useCreateDiscussion();
  const tutorDiscussions = (discussions.data ?? []).filter((d) => d.tutorId === summary.tutorId);
  const unreadCount = tutorDiscussions.filter((d) => hasUnreadDiscussion(d, 'student')).length;

  // Contact déjà connu (CE prof) : aucune sélection, aucun titre à saisir —
  // l'IA le déduira du contenu dès le 1er message (discussion-subject.ts).
  async function handleNewDiscussion() {
    if (!user) return;
    setDiscussionError(null);
    try {
      const discussion = await createDiscussion.mutateAsync({
        tutorId: summary.tutorId,
        studentId: user.id,
        subject: { scope: 'free' },
      });
      void navigate({ to: '/discussions/$discussionId', params: { discussionId: discussion.id } });
    } catch (err) {
      setDiscussionError(
        err instanceof DiscussionError ? err.message : "Impossible d'ouvrir la discussion.",
      );
    }
  }

  const activeItems = summary.assignments.filter((a) => a.status !== 'completed');

  return (
    <Card className="border-ink-100">
      <CardHeader className="pb-2 pt-4">
        {/* Badges SOUS le nom : sur 375 px ils tronquaient le nom du prof (« M. … »). */}
        <div className="flex min-w-0 items-center gap-3">
          <AvatarCircle name={summary.tutorName} src={summary.tutorAvatarUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <CardTitle className="font-display text-ink-900 truncate text-lg font-bold">
              {summary.tutorName}
            </CardTitle>
            <p className="text-ink-500 mt-0.5 truncate text-sm">
              {summary.subjects.length === 0
                ? 'Toutes matières'
                : summary.subjects
                    .map((s) => SUBJECTS.find((sub) => sub.value === s)?.label ?? s)
                    .join(', ')}
            </p>
            {(activeItems.length > 0 || summary.completedCount > 0 || unreadCount > 0) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {activeItems.length > 0 && (
                  <span className="ocean:bg-[#3a2f0c] ocean:text-[#f7d069] ocean:ring-1 ocean:ring-[#8a6a1a] flex items-center gap-1 rounded-full bg-[#fde68a] px-2.5 py-0.5 text-xs font-bold text-[#5b4300]">
                    <ClipboardList className="h-3.5 w-3.5" aria-hidden />
                    {activeItems.length} à faire
                  </span>
                )}
                {summary.completedCount > 0 && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    {summary.completedCount} fait
                    {summary.completedCount > 1 ? 's' : ''}
                  </span>
                )}
                {unreadCount > 0 && (
                  <span
                    aria-label={`${unreadCount} message${unreadCount > 1 ? 's' : ''} non lu${unreadCount > 1 ? 's' : ''}`}
                    className="border-primary-200 bg-primary-100 text-primary-700 rounded-full border px-2 py-0.5 text-xs font-medium"
                  >
                    💬 {unreadCount}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pb-3">
        {activeItems.length > 0 && (
          <div>
            <button
              type="button"
              className="text-primary-600 mb-2 flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
              onClick={() => setExpanded((v) => !v)}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                aria-hidden
              />
              {activeItems.length} exercice
              {activeItems.length > 1 ? 's' : ''} assigné
              {activeItems.length > 1 ? 's' : ''}
            </button>
            {expanded && (
              <div className="space-y-2">
                {activeItems.map((item) => (
                  <div
                    key={item.id}
                    className="border-ink-100 bg-ink-50 flex items-center justify-between gap-2 rounded-xl border p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-900 truncate text-sm font-medium">
                        {item.exerciseTitle}
                      </p>
                      {item.dueAt && (
                        <p className="text-ink-400 mt-0.5 text-xs">
                          Avant le {new Date(item.dueAt).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <ExerciseAccessBadge
                        accessState={{ allowed: true, reason: 'tutor_assignment' }}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onStartExercise(item.exerciseId)}
                        loading={pendingExerciseId === item.exerciseId}
                        disabled={pendingExerciseId !== null}
                      >
                        {pendingExerciseId === item.exerciseId ? 'Ouverture…' : 'Lancer'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            variant="primary"
            size="lg"
            onClick={onSendExercise}
            className="w-full sm:w-auto sm:flex-1"
          >
            <span className="flex items-center justify-center gap-2">
              <Camera className="h-5 w-5" /> Envoyer un exercice à mon prof
            </span>
          </Button>

          <div className="flex items-center justify-center gap-2">
            {/* Fichiers partagés avec CE prof (migration 0261) — dans les deux sens. */}
            <button
              type="button"
              aria-expanded={showFiles}
              aria-label={`Fichiers partagés avec ${summary.tutorName}`}
              title="Fichiers partagés"
              onClick={() => setShowFiles((v) => !v)}
              className={cn(
                'flex h-11 min-h-[2.75rem] w-11 min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl transition-colors active:scale-95',
                showFiles
                  ? 'bg-primary-500 text-white'
                  : 'bg-primary-50 text-primary-600 hover:bg-primary-100',
              )}
            >
              <Paperclip className="h-5 w-5" aria-hidden />
            </button>

            {/* Discussions avec CE prof (migrations 0265-0267). */}
            <button
              type="button"
              aria-expanded={showDiscussions}
              aria-label={`Discussions avec ${summary.tutorName}`}
              title="Discussions"
              onClick={() => setShowDiscussions((v) => !v)}
              className={cn(
                'relative flex h-11 min-h-[2.75rem] w-11 min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl transition-colors active:scale-95',
                showDiscussions
                  ? 'bg-primary-500 text-white'
                  : 'bg-primary-50 text-primary-600 hover:bg-primary-100',
              )}
            >
              <MessageCircle className="h-5 w-5" aria-hidden />
              {unreadCount > 0 && (
                <span
                  aria-hidden
                  className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-[#ef4444] ring-2 ring-white"
                />
              )}
            </button>

            <button
              type="button"
              onClick={() => void handleNewDiscussion()}
              disabled={createDiscussion.isPending}
              aria-label={`Nouvelle discussion avec ${summary.tutorName}`}
              title="Nouvelle discussion"
              className="bg-primary-50 text-primary-600 hover:bg-primary-100 flex h-11 min-h-[2.75rem] w-11 min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl transition-colors active:scale-95 disabled:opacity-50"
            >
              <MessageCirclePlus className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>

        {discussionError && (
          <p role="alert" className="text-accent-700 text-xs font-medium">
            {discussionError}
          </p>
        )}
        {showFiles && (
          <DocumentsPanel
            target={{ kind: 'contact', peerId: summary.tutorId }}
            canUpload
            emptyLabel={`Aucun fichier partagé avec ${summary.tutorName}.`}
          />
        )}
        {showDiscussions && user && (
          <DiscussionsList
            discussions={tutorDiscussions}
            myRole="student"
            emptyLabel={`Aucune discussion avec ${summary.tutorName}.`}
            onOpen={(id) =>
              void navigate({ to: '/discussions/$discussionId', params: { discussionId: id } })
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
